// 보상 버튼 한 번 — 게이트 판정과 발급 → 시청 → 청구. 화면은 이 훅만 안다
import { useEffect, useRef, useState } from 'react';

import { sessionStore, useAdGate, useSession } from '../../../composition/plugin-runtime';
import { showRewardedAd } from '../adapters/adsense';
import { claimTicket, issueTicket } from '../adapters/ticket-api';
import { decideGate, type GateConfig } from '../domain/gate';
import { watchForReward, type RewardResult } from '../domain/reward-flow';

export interface RewardedAdParams extends GateConfig {
  /** MVP API 의 티켓 창구 */
  readonly endpoint: string;
}

export function useRewardedAd(params: RewardedAdParams) {
  const session = useSession();
  const gate = decideGate(params, useAdGate());
  // 광고가 떠 있는 동안 다시 누르면 티켓 요청만 늘어난다
  const running = useRef(false);
  // 광고를 받는 사이 게이트가 닫히면 띄우지 않는다 — 누른 시점의 판정이 아니라 지금 판정을 본다
  const open = useRef(false);
  const visible = gate.open && session !== null;
  useEffect(() => {
    open.current = visible;
    return () => {
      open.current = false; // 화면을 떠났다
    };
  }, [visible]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<RewardResult | null>(null);

  async function watch(): Promise<void> {
    if (!session || !visible || running.current) return;
    running.current = true;
    setBusy(true);
    setResult(null);
    try {
      setResult(
        await watchForReward({
          issue: () => issueTicket(params.endpoint, session.token),
          show: () => showRewardedAd(params.client, params.slot, { allowed: () => open.current }),
          // 광고를 보는 사이 토큰이 새로 발급됐으면 새 것으로 청구한다
          claim: ticket => claimTicket(params.endpoint, sessionStore.get()?.token ?? session.token, ticket),
        }),
      );
    } finally {
      running.current = false;
      setBusy(false);
    }
  }

  // 로그인하지 않았으면 티켓을 받을 수 없다 — 게이트가 열려 있어도 버튼을 보이지 않는다
  return { visible, busy, result, watch };
}
